import React, { useState, useEffect } from 'react';

export default function ProductForm({ boutiqueSlug, productId, categories = [], gallery = [] }) {
    const [formData, setFormData] = useState({
        name: '',
        price: '',
        stock: '',
        category: '',
        description: '',
        isActive: true
    });
    const [errors, setErrors] = useState({});
    const [touched, setTouched] = useState({});
    const [isSubmitting, setIsSubmitting] = useState(false);

    // Initialisation
    useEffect(() => {
        if (productId) {
            // Dans un vrai cas, on ferait un fetch ici pour récupérer les infos du produit
            // fetch(`/api-boutique/${boutiqueSlug}/product/${productId}`).then(...)
        }
    }, [productId, boutiqueSlug]);

    // Règles de validation
    const rules = {
        name: { required: true, minLength: 3, maxLength: 100 },
        price: { required: true, min: 0 },
        stock: { required: true, min: 0 },
        category: { required: true },
        description: { required: false, maxLength: 1000 }
    };

    // Fonction de validation temps réel
    const validate = (name, value) => {
        let error = '';
        const rule = rules[name];
        if (!rule) return '';

        if (rule.required && (value === '' || value === null || value === undefined)) {
            error = 'Ce champ est requis';
        } else if (rule.minLength && value.toString().length < rule.minLength) {
            error = `Minimum ${rule.minLength} caractères`;
        } else if (rule.maxLength && value.toString().length > rule.maxLength) {
            error = `Maximum ${rule.maxLength} caractères`;
        } else if (rule.min !== undefined && Number(value) < rule.min) {
            error = `La valeur doit être au moins ${rule.min}`;
        }
        return error;
    };

    // Gestion de la frappe (Temps réel)
    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;
        const val = type === 'checkbox' ? checked : value;
        setFormData(prev => ({ ...prev, [name]: val }));
        
        const error = validate(name, val);
        setErrors(prev => ({ ...prev, [name]: error }));
        setTouched(prev => ({ ...prev, [name]: true }));
    };

    // Gestion du blur
    const handleBlur = (e) => {
        const { name, value } = e.target;
        setTouched(prev => ({ ...prev, [name]: true }));
        setErrors(prev => ({ ...prev, [name]: validate(name, value) }));
    };

    // Soumission du formulaire
    const handleSubmit = async (e) => {
        e.preventDefault();
        
        // Tout valider
        const newErrors = {};
        let isValid = true;
        Object.keys(rules).forEach(key => {
            const error = validate(key, formData[key]);
            if (error) {
                newErrors[key] = error;
                isValid = false;
            }
        });
        
        setErrors(newErrors);
        setTouched(Object.keys(rules).reduce((acc, key) => ({...acc, [key]: true}), {}));

        if (!isValid) return;

        setIsSubmitting(true);
        
        try {
            // Appel API simulé
            // await fetch(`/api-boutique/${boutiqueSlug}/product/create`, { ... })
            setTimeout(() => {
                setIsSubmitting(false);
                window.location.href = `/my-account/boutique/${boutiqueSlug}/products`;
            }, 1000);
        } catch (err) {
            setIsSubmitting(false);
            alert("Erreur lors de l'enregistrement");
        }
    };

    // Helper pour générer les champs UI-UX Pro Max + Validation
    const renderInput = (name, label, type = 'text', maxLength = null, additionalProps = {}) => {
        const value = formData[name];
        const error = errors[name];
        const isTouched = touched[name];
        const isValid = isTouched && !error;
        const isInvalid = isTouched && !!error;
        
        let className = "form-control shadow-sm transition-all";
        if (isValid) className += " is-valid";
        if (isInvalid) className += " is-invalid";

        return (
            <div className="form-group mb-4" style={{ position: 'relative' }}>
                <label htmlFor={name} className="form-label font-rubik text-slate-900 fw-semibold">{label}</label>
                {type === 'textarea' ? (
                    <textarea 
                        id={name} name={name} className={className} 
                        value={value} onChange={handleChange} onBlur={handleBlur}
                        maxLength={maxLength} rows="4" 
                        style={{ resize: 'none', backgroundColor: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: '10px' }}
                        {...additionalProps}
                    />
                ) : type === 'select' ? (
                    <select 
                        id={name} name={name} className={className} 
                        value={value} onChange={handleChange} onBlur={handleBlur} 
                        style={{ backgroundColor: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: '10px', height: '45px' }}
                        {...additionalProps}
                    >
                        <option value="">-- Sélectionner --</option>
                        {additionalProps.options?.map(opt => (
                            <option key={opt.id} value={opt.id}>{opt.name}</option>
                        ))}
                    </select>
                ) : (
                    <input 
                        type={type} id={name} name={name} className={className} 
                        value={value} onChange={handleChange} onBlur={handleBlur}
                        maxLength={maxLength} 
                        style={{ backgroundColor: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: '10px', height: '45px' }}
                        {...additionalProps}
                    />
                )}
                
                {/* Compteur de caractères (Form Realtime Validation) */}
                {maxLength && (
                    <span className="char-counter transition-all" style={{ fontSize: '0.75rem', color: isInvalid ? '#dc3545' : '#64748b', position: 'absolute', right: '5px', bottom: '-20px', fontWeight: 600 }}>
                        {value ? value.toString().length : 0} / {maxLength}
                    </span>
                )}
                
                {/* Feedback d'erreur */}
                {isInvalid && (
                    <div className="validation-feedback text-danger slide-down" style={{ fontSize: '0.8rem', position: 'absolute', left: '5px', bottom: '-20px', fontWeight: 500, animation: 'slideDown 0.2s ease-out' }}>
                        {error}
                    </div>
                )}
            </div>
        );
    };

    return (
        <form onSubmit={handleSubmit} noValidate>
            <style>{`
                @keyframes slideDown { from { opacity: 0; transform: translateY(-5px); } to { opacity: 1; transform: translateY(0); } }
                .form-control:focus { box-shadow: 0 0 0 0.25rem rgba(var(--boutique-primary-rgb, 34,197,94), 0.25) !important; border-color: var(--boutique-primary, #22C55E) !important; background-color: #ffffff !important; }
                .is-valid { border-color: #22C55E !important; }
                .is-invalid { border-color: #EF4444 !important; }
            `}</style>
            
            <div className="row g-4">
                <div className="col-md-8">
                    <div className="card shadow-sm border-0 rounded-4 h-100">
                        <div className="card-body p-4 p-md-5">
                            <h5 className="font-rubik text-slate-900 mb-4 d-flex align-items-center">
                                <div style={{width:'35px', height:'35px', borderRadius:'8px', background:'var(--boutique-secondary-soft)', color:'var(--boutique-secondary)', display:'flex', alignItems:'center', justifyContent:'center', marginRight:'15px'}}>
                                    <i className="fas fa-info-circle"></i>
                                </div>
                                Informations Générales
                            </h5>
                            
                            {renderInput('name', 'Nom du produit', 'text', 100, { placeholder: 'Ex: T-shirt en coton bio' })}
                            {renderInput('description', 'Description détaillée', 'textarea', 1000, { placeholder: 'Décrivez votre produit...' })}
                            
                            <div className="row mt-2">
                                <div className="col-md-6">
                                    {renderInput('price', 'Prix unitaire (FCFA)', 'number', null, { min: 0, placeholder: 'Ex: 15000' })}
                                </div>
                                <div className="col-md-6">
                                    {renderInput('stock', 'Quantité en stock', 'number', null, { min: 0, placeholder: 'Ex: 50' })}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
                
                <div className="col-md-4">
                    <div className="card shadow-sm border-0 rounded-4 mb-4">
                        <div className="card-body p-4">
                            <h5 className="font-rubik text-slate-900 mb-4 d-flex align-items-center">
                                <div style={{width:'35px', height:'35px', borderRadius:'8px', background:'var(--boutique-secondary-soft)', color:'var(--boutique-secondary)', display:'flex', alignItems:'center', justifyContent:'center', marginRight:'15px'}}>
                                    <i className="fas fa-folder-open"></i>
                                </div>
                                Organisation
                            </h5>
                            {renderInput('category', 'Catégorie', 'select', null, { options: categories })}
                            
                            <hr className="my-4" style={{ borderColor: '#e2e8f0' }} />
                            
                            <div className="form-check form-switch mb-3 p-3 rounded-3" style={{ background: '#f8fafc', border: '1px solid #f1f5f9' }}>
                                <input className="form-check-input" type="checkbox" role="switch" id="isActive" name="isActive" checked={formData.isActive} onChange={handleChange} style={{ cursor: 'pointer', height: '24px', width: '48px', marginRight: '15px' }}/>
                                <div>
                                    <label className="form-check-label font-rubik text-slate-900 fw-semibold" htmlFor="isActive" style={{ cursor: 'pointer' }}>Produit actif</label>
                                    <div className="text-muted" style={{ fontSize: '0.8rem' }}>Rend le produit visible sur la boutique</div>
                                </div>
                            </div>
                        </div>
                    </div>
                    
                    <button type="submit" disabled={isSubmitting} className="btn w-100 py-3 rounded-pill shadow-sm hover-lift font-rubik d-flex justify-content-center align-items-center text-white" style={{ backgroundColor: 'var(--boutique-primary)', border: 'none', fontSize: '1.1rem' }}>
                        {isSubmitting ? (
                            <><span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span> Traitement...</>
                        ) : (
                            <><i className="fas fa-check-circle me-2"></i> {productId ? 'Mettre à jour' : 'Enregistrer le produit'}</>
                        )}
                    </button>
                    
                    <div className="text-center mt-3">
                        <a href={`/my-account/boutique/${boutiqueSlug}/products`} className="text-muted text-decoration-none hover-lift d-inline-block" style={{ fontSize: '0.9rem' }}>Annuler</a>
                    </div>
                </div>
            </div>
        </form>
    );
}
